import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Target, 
  Eye, 
  Scale, 
  MapPin,
  ArrowRight,
  BookOpen,
  CheckCircle,
  Users,
  Crown,
  Star,
  Award
} from 'lucide-react';

const legalFrameworks = [
  'Universal Declaration of Human Rights (Article 20)',
  'International Covenant on Civil and Political Rights (Article 22)',
  'European Convention on Human Rights (Article 11)',
  'UNESCO World Declaration on Higher Education (1998)',
  'UNESCO WHEC Declaration (2022)',
  'United Nations Sustainable Development Goal 4 – Quality Education',
  'Bologna Process and the European Higher Education Area (EHEA)',
];

// Leadership data
const president = {
  name: 'Prof. Dr. Bakhtiyar Najafov',
  title: 'President',
  photo: '/founders/bahtiyar.jpeg'
};

const vicePresidents = [
  { name: 'Prof. Dr. Erdal Dursun', title: 'Vice President', photo: '/founders/erdal.jpeg' },
  { name: 'Prof. Dr. Imran Akperov', title: 'Vice President', photo: '/founders/imran_akperov.jpg' },
  { name: 'Prof. Dr. Dmitriy Škyev', title: 'Vice President', photo: '/founders/dimitri.jpeg' },
  { name: 'Dr. Mehmet Macar', title: 'Vice President', photo: '/founders/mehmet-macar.jpg' },
];

const generalSecretary = {
  name: 'Ahmed Darwish',
  title: 'General Secretary',
  photo: '/founders/ahmed-darwish.jpeg'
};

const boardMembers = [
  { name: 'Ramal Shamshadli', title: 'Member of the Board of Directors', photo: '/founders/ramal-shamshadli.jpeg' },
  { name: 'Farid Hasanzada', title: 'Member of the Board of Directors', photo: '/founders/ferid.jpg' },
  { name: 'Miryusif Javadzade', title: 'Member of the Board of Directors', photo: '/founders/miryusif.jpeg' },
  { name: 'Mikayil Mirzazada', title: 'Member of the Board of Directors', photo: '/founders/mikayiil.jpg' },
  { name: 'Rashad Abbasov', title: 'Member of the Board of Directors', photo: '/founders/rashad-abbasov.jpeg' },
  { name: 'Huseyin Vagıfzada', title: 'Member of the Board of Directors', photo: '/founders/huseyin-vagifzada.jpeg' },
];

export default function AboutPage() {
  return (
    <div>
      {/* Hero Section */}
      <section className="bg-[#0E285B] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 mb-6">
              <img src="/logo-new.png" alt="UIEHEC" className="h-8 w-auto" />
              <span className="text-[#F5A623] font-medium uppercase tracking-wider text-sm">
                About UIEHEC
              </span>
            </div>
            <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold text-white leading-tight mb-6">
              Who We Are
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed">
              The Union of International Education and Higher Education Centers (UIEHEC) was founded in Aachen, 
              Federal Republic of Germany, as a visionary alliance bringing together higher education institutions, 
              think tanks, research centers, and organizations dedicated to advancing the global education agenda.
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Mission */}
            <div className="bg-white p-8 border border-slate-100 shadow-sm" data-testid="mission-section">
              <div className="flex items-center gap-4 mb-6">
                <div className="h-14 w-14 bg-[#0E285B] rounded-sm flex items-center justify-center">
                  <Target className="h-7 w-7 text-[#F5A623]" />
                </div>
                <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#0E285B]">
                  Our Mission
                </h2>
              </div>
              <p className="text-slate-600 leading-relaxed">
                To build a dynamic, collaborative, and innovative international network that strengthens quality, 
                accessibility, and sustainability in higher education through strategic partnerships and academic cooperation.
              </p>
            </div>

            {/* Vision */}
            <div className="bg-white p-8 border border-slate-100 shadow-sm" data-testid="vision-section">
              <div className="flex items-center gap-4 mb-6">
                <div className="h-14 w-14 bg-[#0E285B] rounded-sm flex items-center justify-center">
                  <Eye className="h-7 w-7 text-[#F5A623]" />
                </div>
                <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#0E285B]">
                  Our Vision
                </h2>
              </div>
              <p className="text-slate-600 leading-relaxed">
                To become a leading global platform that connects education leaders and institutions, 
                inspiring transformation, innovation, and excellence in higher education worldwide.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Legal Foundation */}
      <section className="py-20 md:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <div className="flex items-center gap-4 mb-6">
                <div className="h-14 w-14 bg-[#0E285B] rounded-sm flex items-center justify-center">
                  <Scale className="h-7 w-7 text-[#F5A623]" />
                </div>
                <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#0E285B]">
                  Legal Foundation
                </h2>
              </div>
              <p className="text-slate-600 leading-relaxed mb-6">
                UIEHEC operates in line with the following key international frameworks and declarations:
              </p>
              <ul className="space-y-3">
                {legalFrameworks.map((framework, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-[#F5A623] mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600">{framework}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Head Office */}
            <div className="bg-white p-8 border border-slate-100 shadow-sm">
              <div className="flex items-center gap-4 mb-6">
                <div className="h-14 w-14 bg-[#0E285B] rounded-sm flex items-center justify-center">
                  <MapPin className="h-7 w-7 text-[#F5A623]" />
                </div>
                <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#0E285B]">
                  Head Office
                </h2>
              </div>
              <address className="text-slate-600 not-italic space-y-2">
                <p className="font-semibold text-[#0E285B]">
                  Union of International Education and Higher Education Centers (UIEHEC)
                </p>
                <p>Heinrichsallee 18</p>
                <p>52062 Aachen</p>
                <p>Federal Republic of Germany</p>
                <div className="pt-4 border-t border-slate-200 mt-4">
                  <p>Email: <a href="mailto:info@uiehec.org" className="text-[#F5A623] hover:underline">info@uiehec.org</a></p>
                  <p>Phone: <a href="tel:+4916059429555" className="text-[#F5A623] hover:underline">+49 160 594 29 55</a></p>
                </div>
              </address>
            </div>
          </div>
        </div>
      </section>

      {/* Leadership / Yönetim Section */}
      <section className="py-20 md:py-28 bg-white" data-testid="leadership-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Users className="h-6 w-6 text-[#F5A623]" />
              <span className="text-[#F5A623] font-medium uppercase tracking-wider text-sm">
                Leadership
              </span>
            </div>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#0E285B] mb-4">
              Board of Directors
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              UIEHEC is led by a distinguished team of education leaders and experts from around the world.
            </p>
          </div>

          {/* President */}
          <div className="mb-16">
            <div className="flex items-center gap-2 mb-8">
              <Crown className="h-5 w-5 text-[#F5A623]" />
              <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#0E285B]">President</h3>
            </div>
            <div className="flex justify-center">
              <div className="bg-white p-8 rounded-sm shadow-sm border border-slate-100 max-w-sm text-center">
                <div className="relative mb-4 mx-auto" style={{ width: '160px', height: '160px' }}>
                  <img 
                    src={president.photo} 
                    alt={president.name}
                    className="w-full h-full rounded-full object-cover border-4 border-[#F5A623] shadow-lg"
                  />
                </div>
                <h4 className="font-['Playfair_Display'] text-xl font-semibold text-[#0E285B] mb-1">
                  {president.name}
                </h4>
                <p className="text-[#F5A623] font-medium">{president.title}</p>
              </div>
            </div>
          </div>

          {/* Vice Presidents */}
          <div className="mb-16">
            <div className="flex items-center gap-2 mb-8">
              <Star className="h-5 w-5 text-[#F5A623]" />
              <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#0E285B]">Vice Presidents</h3>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {vicePresidents.map((person, index) => (
                <div key={index} className="bg-white p-6 rounded-sm shadow-sm border border-slate-100 text-center hover:shadow-md transition-shadow">
                  <div className="relative mb-4 mx-auto overflow-hidden rounded-full border-4 border-slate-100 shadow" style={{ width: '120px', height: '120px' }}>
                    <img 
                      src={person.photo} 
                      alt={person.name}
                      className="w-full h-full object-cover"
                      style={{ 
                        objectPosition: person.name === 'Dr. Mehmet Macar' ? 'center center' : 'center 20%',
                        transform: person.name === 'Dr. Mehmet Macar' ? 'scale(1.4)' : 'scale(1)'
                      }}
                    />
                  </div>
                  <h4 className="font-['Playfair_Display'] text-lg font-semibold text-[#0E285B] mb-1">
                    {person.name}
                  </h4>
                  <p className="text-[#F5A623] text-sm font-medium">{person.title}</p>
                </div>
              ))}
            </div>
          </div>

          {/* General Secretary */}
          <div className="mb-16">
            <div className="flex items-center gap-2 mb-8">
              <Award className="h-5 w-5 text-[#F5A623]" />
              <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#0E285B]">General Secretary</h3>
            </div>
            <div className="flex justify-center">
              <div className="bg-slate-50 p-6 rounded-sm shadow-sm border border-slate-100 text-center max-w-xs">
                <div className="relative mb-4 mx-auto overflow-hidden rounded-full border-4 border-[#0E285B] shadow" style={{ width: '130px', height: '130px' }}>
                  <img 
                    src={generalSecretary.photo} 
                    alt={generalSecretary.name}
                    className="w-full h-full object-cover"
                    style={{ objectPosition: 'center 20%' }}
                  />
                </div>
                <h4 className="font-['Playfair_Display'] text-lg font-semibold text-[#0E285B] mb-1">
                  {generalSecretary.name}
                </h4>
                <p className="text-[#F5A623] text-sm font-medium">{generalSecretary.title}</p>
              </div>
            </div>
          </div>

          {/* Board Members */}
          <div>
            <div className="flex items-center gap-2 mb-8">
              <Users className="h-5 w-5 text-[#F5A623]" />
              <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#0E285B]">Board Members</h3>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {boardMembers.map((person, index) => (
                <div key={index} className="bg-white p-6 rounded-sm shadow-sm border border-slate-100 text-center hover:shadow-md transition-shadow">
                  <div className="relative mb-4 mx-auto overflow-hidden rounded-full border-4 border-slate-100 shadow" style={{ width: '100px', height: '100px' }}>
                    <img 
                      src={person.photo} 
                      alt={person.name}
                      className="w-full h-full object-cover"
                      style={{ objectPosition: 'center 20%' }}
                    />
                  </div>
                  <h4 className="font-['Playfair_Display'] text-base font-semibold text-[#0E285B] mb-1">
                    {person.name}
                  </h4>
                  <p className="text-slate-500 text-xs">Member of the Board of Directors</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#0E285B]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <BookOpen className="h-16 w-16 text-[#F5A623] mx-auto mb-6" />
          <h2 className="font-['Playfair_Display'] text-3xl font-bold text-white mb-6">
            Join Our Growing Community
          </h2>
          <p className="text-slate-300 mb-8 max-w-2xl mx-auto">
            UIEHEC continues to grow, welcoming new member institutions from around the world. 
            Our annual conferences, workshops, and collaborative projects create opportunities 
            for meaningful exchange and partnership.
          </p>
          <Link to="/apply">
            <Button 
              size="lg"
              className="bg-[#F5A623] text-[#0E285B] hover:bg-[#F5A623]/90 h-12 px-8 rounded-sm font-semibold"
              data-testid="about-apply-btn"
            >
              Apply for Membership
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
